import type { MeetingInviteEvent } from '@ai-meeting/shared'
import { fmt, type UiStrings } from '@/constants/ui-locale'
import LanguagePair from './LanguagePair'

/**
 * One meeting running right now that this reader was invited to.
 *
 * Shaped like the Recent Meetings row below it so the two read as one list, with one difference
 * that matters: this is not a link. The meeting belongs to whoever shared it, and opening it would
 * be opening somebody else's — the API answers a recipient's writes with 403. Joining instead
 * creates a meeting of the reader's own in the same room, which is what the button does.
 *
 * Purely presentational. Whether the room is still live is asked at the press, by
 * `useJoinMeetingInvite`, and this renders whichever answer came back.
 *
 * @param invite The room, as the server pushed it.
 * @param isChecking True while the server is being asked whether the room is still joinable.
 * @param hasEnded True once it has answered that it is not.
 * @param onJoin Pressed Join.
 */
export default function OngoingMeetingRow({
  invite,
  isChecking,
  hasEnded,
  onJoin,
  t,
}: {
  invite: MeetingInviteEvent
  isChecking: boolean
  hasEnded: boolean
  onJoin: () => void
  t: UiStrings
}) {
  return (
    <div className="grid min-h-[76px] grid-cols-[auto_1fr] items-center gap-3 rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] px-4 py-3 shadow-sm sm:grid-cols-[auto_minmax(0,1fr)_auto_auto] sm:gap-5 sm:px-5">
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-emerald-100 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-400">
        <LiveGlyph />
      </div>

      <div className="min-w-0">
        <div className="flex min-w-0 items-center gap-2">
          <h3 className="truncate text-sm font-semibold text-[var(--text-primary)]">
            {invite.title}
          </h3>
          {!hasEnded && (
            <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400">
              <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden="true" />
              {t.meetingLiveBadge}
            </span>
          )}
        </div>
        <p className="mt-0.5 text-xs text-[var(--text-secondary)]">
          {hasEnded
            ? t.meetingInviteEnded
            : fmt(t.meetingInviteFrom, { name: invite.sharedBy.name })}
        </p>
      </div>

      <div className="col-start-2 row-start-2 sm:col-auto sm:row-auto">
        <LanguagePair targetLang={invite.targetLang} />
      </div>

      {/* Dropped once the room has ended rather than disabled: there is nothing left to press, and
          a greyed-out button invites the reader to keep trying. */}
      {!hasEnded && (
        <div className="col-start-2 row-start-3 sm:col-auto sm:row-auto">
          <button
            type="button"
            disabled={isChecking}
            onClick={onJoin}
            className="btn-primary w-full px-3 py-1.5 text-xs disabled:opacity-50 sm:w-auto"
          >
            {isChecking ? t.meetingInviteChecking : t.meetingInviteJoin}
          </button>
        </div>
      )}
    </div>
  )
}

/** A broadcast dot, which says "happening now" where the Recent rows show a document. */
function LiveGlyph() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="2.5" fill="currentColor" stroke="none" />
      <path strokeLinecap="round" d="M8.2 8.2a5.4 5.4 0 0 0 0 7.6M15.8 15.8a5.4 5.4 0 0 0 0-7.6" />
      <path strokeLinecap="round" d="M5.4 5.4a9.4 9.4 0 0 0 0 13.2M18.6 18.6a9.4 9.4 0 0 0 0-13.2" />
    </svg>
  )
}
